import type { MemberIdentifierType } from "domains/inventory/services/types";
import type { CarrierMasterBundleState } from "domains/inventory/utils/carrierMasterCode";
import type {
  LockerAccessCodeType,
  MasterAccessCodeType,
} from "domains/inventory/utils/accessCode";

export type FlowStep =
  | "idle"
  | "identify"
  | "select"
  | "detail"
  | "confirm"
  | "return"
  | "supply"
  | "bundle"
  | "complete";

export type FlowMode = "rental" | "return" | "supply" | "code_entry" | "bundle";

export type FlowError =
  | "invalid_code"
  | "expired_code"
  | "used_code"
  | "return_before_pickup"
  | "door_open_failed"
  | "cancelled_code"
  | "no_eligible_reservations"
  | "unsupported_code_type"
  | "lookup_unavailable"
  | "missing_item";

export interface InventoryFlowItem {
  id: string;
  name: string;
  description?: string | null;
  categoryName?: string | null;
  imageUrl?: string | null;
  lockerDoorId?: string | number | null;
  isReturnRequired?: boolean | number | string | null;
}

export interface InventoryFlowState {
  step: FlowStep;
  mode: FlowMode;
  flowId: string;
  code?: number;
  codeStatus?: "active" | "used";
  codeType?: LockerAccessCodeType;
  orderId?: string;
  inventoryId?: string;
  item?: InventoryFlowItem;
  lockerDoorId?: string;
  doorId?: string;
  returnMethod?: string;
  bundle?: CarrierMasterBundleState;
  doorOpenFailed?: boolean;
  error?: FlowError;
  contact?: string | null;
  memberId?: string | null;
  identifierType?: MemberIdentifierType | null;
  identifier?: string | null;
  updatedAt: string;
}

export type InventoryFlowAction =
  | { type: "START_RENTAL" }
  | {
      type: "SET_IDENTITY";
      payload: {
        contact: string | null;
        memberId: string | null;
        identifierType: MemberIdentifierType | null;
        identifier: string | null;
      };
    }
  | {
      type: "SELECT_ITEM";
      payload: { item: InventoryFlowItem; inventoryId: string };
    }
  | {
      type: "CREATE_ORDER_SUCCESS";
      payload: {
        orderId?: string;
        code?: number;
        lockerDoorId?: string;
        returnMethod?: string;
      };
    }
  | {
      type: "ENTER_CODE_SUCCESS";
      payload: {
        mode: FlowMode;
        step: FlowStep;
        code?: number;
        codeStatus?: "active" | "used";
        codeType?: LockerAccessCodeType;
        orderId?: string;
        inventoryId?: string;
        lockerDoorId?: string;
        doorId?: string;
        returnMethod?: string;
      };
    }
  | {
      type: "ENTER_BUNDLE_CODE_SUCCESS";
      payload: {
        codeStatus: "active" | "used";
        codeType: MasterAccessCodeType;
        bundle: CarrierMasterBundleState;
      };
    }
  | {
      type: "OPEN_DOOR_RESULT";
      payload: {
        doorId?: string;
        doorOpenFailed?: boolean;
      };
    }
  | { type: "REPORT_USAGE_SUCCESS" }
  | { type: "SET_ERROR"; payload: { error: FlowError } }
  | { type: "MERGE_STATE"; payload: Partial<InventoryFlowState> }
  | { type: "CLEAR_FLOW" };

const createFlowId = () => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return String(Date.now());
};

export const createInitialFlowState = (): InventoryFlowState => ({
  step: "idle",
  mode: "rental",
  flowId: createFlowId(),
  updatedAt: new Date().toISOString(),
});

const withUpdatedAt = (state: InventoryFlowState): InventoryFlowState => ({
  ...state,
  updatedAt: new Date().toISOString(),
});

export function inventoryFlowReducer(
  state: InventoryFlowState,
  action: InventoryFlowAction
): InventoryFlowState {
  switch (action.type) {
    case "START_RENTAL":
      return {
        ...createInitialFlowState(),
        mode: "rental",
        step: "identify",
      };
    case "SET_IDENTITY":
      return withUpdatedAt({
        ...state,
        step: "select",
        error: undefined,
        contact: action.payload.contact,
        memberId: action.payload.memberId,
        identifierType: action.payload.identifierType,
        identifier: action.payload.identifier,
      });
    case "SELECT_ITEM":
      return withUpdatedAt({
        ...state,
        step: "detail",
        error: undefined,
        item: action.payload.item,
        inventoryId: action.payload.inventoryId,
        lockerDoorId: action.payload.item?.lockerDoorId
          ? String(action.payload.item.lockerDoorId)
          : state.lockerDoorId,
      });
    case "CREATE_ORDER_SUCCESS":
      return withUpdatedAt({
        ...state,
        step: "confirm",
        error: undefined,
        orderId: action.payload.orderId ?? state.orderId,
        code: action.payload.code ?? state.code,
        lockerDoorId: action.payload.lockerDoorId,
        returnMethod: action.payload.returnMethod ?? state.returnMethod,
      });
    case "ENTER_CODE_SUCCESS":
      return withUpdatedAt({
        ...state,
        mode: action.payload.mode,
        step: action.payload.step,
        error: undefined,
        code: action.payload.code ?? state.code,
        codeStatus: action.payload.codeStatus ?? state.codeStatus,
        codeType: action.payload.codeType ?? state.codeType,
        orderId: action.payload.orderId ?? state.orderId,
        inventoryId: action.payload.inventoryId ?? state.inventoryId,
        lockerDoorId: action.payload.lockerDoorId ?? state.lockerDoorId,
        doorId: action.payload.doorId ?? state.doorId,
        returnMethod: action.payload.returnMethod ?? state.returnMethod,
        bundle: undefined,
      });
    case "ENTER_BUNDLE_CODE_SUCCESS":
      return withUpdatedAt({
        ...state,
        mode: "bundle",
        step: "bundle",
        error: undefined,
        code: undefined,
        codeStatus: action.payload.codeStatus,
        codeType: action.payload.codeType,
        orderId: undefined,
        inventoryId: undefined,
        item: undefined,
        lockerDoorId: undefined,
        doorId: undefined,
        returnMethod: undefined,
        bundle: action.payload.bundle,
      });
    case "OPEN_DOOR_RESULT":
      return withUpdatedAt({
        ...state,
        doorId: action.payload.doorId ?? state.doorId,
        doorOpenFailed: action.payload.doorOpenFailed ?? false,
        error: action.payload.doorOpenFailed ? "door_open_failed" : state.error,
      });
    case "REPORT_USAGE_SUCCESS":
      return withUpdatedAt({
        ...state,
        codeStatus: "used",
      });
    case "SET_ERROR":
      return withUpdatedAt({
        ...state,
        error: action.payload.error,
      });
    case "MERGE_STATE":
      return withUpdatedAt({
        ...state,
        ...action.payload,
      });
    case "CLEAR_FLOW":
      return createInitialFlowState();
    default:
      return state;
  }
}

export { createFlowId };
